/* eslint-disable react/react-in-jsx-scope */
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { checkIfInFrance, dataProcessingParcel } from "@/libs/utils";
import {
  DndContext,
  DragEndEvent,
  useDraggable,
  useDroppable,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import * as toGeoJSON from "@tmcw/togeojson";
import * as turf from "@turf/turf";
import L from "leaflet";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet/dist/leaflet.css";
import {
  Check,
  FolderPlus,
  GripVertical,
  Pencil,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type DrawnFolder = {
  id: string;
  name: string;
};

export type DrawnPolygon = {
  id: string;
  name: string;
  folderId: string;
  coordinates: Array<[number, number]>;
  cadastre?: {
    codeArr?: string;
    codeCom?: string;
    codeDep?: string;
    codeInsee?: string;
    comAbs?: string;
    contenance?: string | number;
    feuille?: string | number;
    idu?: string;
    nomCom?: string;
    numero?: string;
    section?: string;
    pays?: string;
  };
};

export type DrawnMapValue = {
  version: 1;
  folders: DrawnFolder[];
  polygons: DrawnPolygon[];
  mapView?: {
    center: [number, number];
    zoom: number;
  };
};

const DEFAULT_FOLDER_ID = "folder-default";
const DEFAULT_FOLDER_NAME = "Sans dossier";
const DEFAULT_POLYGON_NAME = "Polygone sans titre";
const POLYGON_VISIBILITY_ZOOM_THRESHOLD = 6;
const SELECTED_POLYGON_VISIBILITY_ZOOM_THRESHOLD = 6;
const CADASTRE_MIN_ZOOM = 15;

const uid = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const getDefaultValue = (): DrawnMapValue => ({
  version: 1,
  folders: [{ id: DEFAULT_FOLDER_ID, name: DEFAULT_FOLDER_NAME }],
  polygons: [],
  mapView: {
    center: [48.8566, 2.3522],
    zoom: 5,
  },
});

const ensureDefaultFolder = (value: DrawnMapValue): DrawnMapValue => {
  const baseFolders = Array.isArray(value.folders) ? value.folders : [];
  const folders = [...baseFolders];
  const hasDefault = folders.some((folder) => folder.id === DEFAULT_FOLDER_ID);
  if (!hasDefault) {
    folders.unshift({ id: DEFAULT_FOLDER_ID, name: DEFAULT_FOLDER_NAME });
  }
  const knownFolderIds = new Set(folders.map((folder) => folder.id));
  (Array.isArray(value.polygons) ? value.polygons : []).forEach((polygon) => {
    const folderId =
      typeof polygon?.folderId === "string" && polygon.folderId.trim()
        ? polygon.folderId.trim()
        : DEFAULT_FOLDER_ID;
    if (!knownFolderIds.has(folderId)) {
      knownFolderIds.add(folderId);
      folders.push({ id: folderId, name: "Dossier" });
    }
  });
  return {
    ...value,
    folders,
  };
};

const normalizeFolderId = (value: any): string => {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return `${value}`;
  return DEFAULT_FOLDER_ID;
};

const stripClosingCoordinate = (coordinates: Array<[number, number]>): Array<[number, number]> => {
  if (coordinates.length < 2) return coordinates;
  const [firstLat, firstLng] = coordinates[0];
  const [lastLat, lastLng] = coordinates[coordinates.length - 1];
  if (firstLat === lastLat && firstLng === lastLng) {
    return coordinates.slice(0, -1);
  }
  return coordinates;
};

const normalizeLatLngCoordinates = (rawCoordinates: any): Array<[number, number]> => {
  if (!Array.isArray(rawCoordinates)) return [];
  const coordinates = rawCoordinates
    .map((point: any) => {
      if (Array.isArray(point) && point.length >= 2) {
        const lat = Number(point[0]);
        const lng = Number(point[1]);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          return [lat, lng] as [number, number];
        }
      }
      if (point && typeof point === "object") {
        const lat = Number(point.lat);
        const lng = Number(point.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          return [lat, lng] as [number, number];
        }
      }
      return null;
    })
    .filter((point): point is [number, number] => Array.isArray(point));
  return stripClosingCoordinate(coordinates);
};

const looksLikeFrenchCadastreParcel = (parcel: any): boolean => Boolean(
  (typeof parcel?.codeInsee === "string" && parcel.codeInsee.trim())
  || (typeof parcel?.idu === "string" && parcel.idu.trim())
  || (typeof parcel?.nomCom === "string" && parcel.nomCom.trim())
  || (
    typeof parcel?.section === "string"
    && parcel.section.trim()
    && parcel.section.trim().toUpperCase() !== "DRAWN"
    && (typeof parcel?.numero === "string" || typeof parcel?.numero === "number")
  )
);

const parseParcelPointToLatLng = (point: any, parcel?: any): [number, number] | null => {
  if (!Array.isArray(point) || point.length < 2) return null;
  const a = Number(point[0]);
  const b = Number(point[1]);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;

  if (looksLikeFrenchCadastreParcel(parcel)) {
    const asLatLngInFrance = checkIfInFrance(a, b);
    const asLngLatInFrance = checkIfInFrance(b, a);
    if (asLatLngInFrance && !asLngLatInFrance) return [a, b];
    if (!asLatLngInFrance && asLngLatInFrance) return [b, a];
  }

  if (Math.abs(a) <= 90 && Math.abs(b) <= 180) {
    return [a, b];
  }
  if (Math.abs(b) <= 90 && Math.abs(a) <= 180) {
    return [b, a];
  }
  return null;
};

const parseParcelCoordinatesToLatLng = (rawCoordinates: any, parcel?: any): Array<[number, number]> => {
  if (!Array.isArray(rawCoordinates)) return [];
  const first = rawCoordinates[0];
  let ring = rawCoordinates;
  if (Array.isArray(first) && Array.isArray(first[0]) && Array.isArray(first[0][0])) {
    ring = first[0];
  } else if (Array.isArray(first) && Array.isArray(first[0])) {
    ring = first;
  }
  if (!Array.isArray(ring)) return [];
  return stripClosingCoordinate(
    ring
      .map((point: any) => parseParcelPointToLatLng(point, parcel))
      .filter((point: any) => Array.isArray(point)),
  );
};

const closeRingAndConvertToLatLng = (coordinates: Array<[number, number]>) => {
  const ring = normalizeLatLngCoordinates(coordinates).map(([lat, lng]) => [lat, lng]);
  if (ring.length === 0) return [];
  const [firstLat, firstLng] = ring[0];
  const [lastLat, lastLng] = ring[ring.length - 1];
  if (firstLat !== lastLat || firstLng !== lastLng) {
    ring.push([firstLat, firstLng]);
  }
  return ring;
};

const sanitizeCadastreMetadata = (raw: any): DrawnPolygon["cadastre"] | undefined => {
  if (!raw || typeof raw !== "object") return undefined;
  const cadastre = {
    codeArr: raw.codeArr ?? raw.code_arr,
    codeCom: raw.codeCom ?? raw.code_com,
    codeDep: raw.codeDep ?? raw.code_dep,
    codeInsee: raw.codeInsee ?? raw.code_insee,
    comAbs: raw.comAbs ?? raw.com_abs,
    contenance: raw.contenance,
    feuille: raw.feuille,
    idu: raw.idu,
    nomCom: raw.nomCom ?? raw.nom_com,
    numero: raw.numero,
    section: raw.section,
    pays: raw.pays,
  };
  if (!cadastre.idu && !cadastre.numero && !cadastre.section && !cadastre.codeInsee) {
    return undefined;
  }
  return cadastre;
};

const getCadastreKey = (cadastre?: DrawnPolygon["cadastre"]): string => {
  if (!cadastre) return "";
  if (typeof cadastre.idu === "string" && cadastre.idu.trim()) return cadastre.idu.trim();
  const codeInsee = typeof cadastre.codeInsee === "string" ? cadastre.codeInsee.trim() : "";
  const section = typeof cadastre.section === "string" ? cadastre.section.trim() : "";
  const numero = typeof cadastre.numero === "string" ? cadastre.numero.trim() : "";
  return [codeInsee, section, numero].filter(Boolean).join("_");
};

const getCadastreName = (cadastre?: DrawnPolygon["cadastre"], fallback = "Parcelle") => {
  if (!cadastre) return fallback;
  const section = typeof cadastre.section === "string" ? cadastre.section.trim() : "";
  const numero = typeof cadastre.numero === "string" ? cadastre.numero.trim() : "";
  const label = `${section}${numero}`.trim();
  return label || fallback;
};

const isAutoCadastrePolygonName = (
  candidateName: string,
  cadastre?: DrawnPolygon["cadastre"],
): boolean => {
  const normalizedName = candidateName.trim().toLowerCase();
  if (!normalizedName) return false;

  const cadastreLabel = getCadastreName(cadastre, "").trim().toLowerCase();
  if (cadastreLabel && normalizedName === cadastreLabel) {
    return true;
  }

  if (normalizedName === "parcelle" || normalizedName.startsWith("parcelle ")) {
    return true;
  }

  // Ex: "AB123", "A 12", "ZD-45" -> labels techniques issus du cadastre
  if (/^[a-z]{1,3}\s*-?\s*\d+[a-z0-9]*$/i.test(candidateName.trim())) {
    return true;
  }

  return false;
};

const resolvePolygonDisplayName = (
  rawName: unknown,
  cadastre?: DrawnPolygon["cadastre"],
): string => {
  const name = typeof rawName === "string" ? rawName.trim() : "";
  if (!name) return DEFAULT_POLYGON_NAME;
  if (isAutoCadastrePolygonName(name, cadastre)) {
    return DEFAULT_POLYGON_NAME;
  }
  return name;
};

const getCoordinatesHash = (coordinates: Array<[number, number]>): string => JSON.stringify(
  normalizeLatLngCoordinates(coordinates).map(([lat, lng]) => [
    Number(lat.toFixed(6)),
    Number(lng.toFixed(6)),
  ]),
);

const parseLegacyFolders = (raw: any): DrawnFolder[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((folder: any) => typeof folder?.id === "string" || typeof folder?.id === "number")
    .map((folder: any) => ({
      id: `${folder.id}`,
      name: typeof folder?.name === "string" && folder.name.trim() ? folder.name.trim() : "Dossier",
    }));
};

const createCadastrePolygonFromLegacyParcel = (parcel: any, index: number): DrawnPolygon | null => {
  const coordinates = parseParcelCoordinatesToLatLng(parcel?.coordinates, parcel);
  if (coordinates.length < 3) return null;
  const cadastre = sanitizeCadastreMetadata(parcel);
  return {
    id: typeof parcel?.id === "string" || typeof parcel?.id === "number" ? `${parcel.id}` : uid(`parcel-${index}`),
    name: resolvePolygonDisplayName(parcel?.name, cadastre),
    folderId: normalizeFolderId(parcel?.folder),
    coordinates,
    cadastre,
  };
};

const mergeCadastrePolygons = (basePolygons: DrawnPolygon[], legacyParcels: any[]): DrawnPolygon[] => {
  if (!Array.isArray(legacyParcels) || legacyParcels.length === 0) return basePolygons;
  const nextPolygons = [...basePolygons];
  const cadastreKeyToIndex = new Map<string, number>();
  const coordinatesHashToIndex = new Map<string, number>();

  nextPolygons.forEach((polygon, index) => {
    const cadastreKey = getCadastreKey(polygon.cadastre);
    if (cadastreKey) {
      cadastreKeyToIndex.set(cadastreKey, index);
    }
    const coordinatesHash = getCoordinatesHash(polygon.coordinates);
    if (coordinatesHash && !coordinatesHashToIndex.has(coordinatesHash)) {
      coordinatesHashToIndex.set(coordinatesHash, index);
    }
  });

  legacyParcels.forEach((parcel, index) => {
    const polygon = createCadastrePolygonFromLegacyParcel(parcel, index);
    if (!polygon) return;
    const key = getCadastreKey(polygon.cadastre);
    if (key && cadastreKeyToIndex.has(key)) {
      const existingIndex = cadastreKeyToIndex.get(key);
      if (typeof existingIndex === "number" && !nextPolygons[existingIndex]?.cadastre) {
        const existing = nextPolygons[existingIndex];
        nextPolygons[existingIndex] = {
          ...existing,
          cadastre: polygon.cadastre,
          name: existing.name === DEFAULT_POLYGON_NAME ? polygon.name : existing.name,
        };
      }
      return;
    }

    const coordinatesHash = getCoordinatesHash(polygon.coordinates);
    const existingByCoordinates = coordinatesHash ? coordinatesHashToIndex.get(coordinatesHash) : undefined;
    if (typeof existingByCoordinates === "number") {
      const existing = nextPolygons[existingByCoordinates];
      nextPolygons[existingByCoordinates] = {
        ...existing,
        cadastre: existing.cadastre ?? polygon.cadastre,
        name: existing.name === DEFAULT_POLYGON_NAME ? polygon.name : existing.name,
      };
      if (key) {
        cadastreKeyToIndex.set(key, existingByCoordinates);
      }
      return;
    }

    nextPolygons.push(polygon);
    const nextIndex = nextPolygons.length - 1;
    if (key) {
      cadastreKeyToIndex.set(key, nextIndex);
    }
    if (coordinatesHash) {
      coordinatesHashToIndex.set(coordinatesHash, nextIndex);
    }
  });

  return nextPolygons;
};

const parseLegacyCoordinates = (locationItem: any): Array<[number, number]> => {
  return normalizeLatLngCoordinates(locationItem);
};

const getPolygonCentroid = (coordinates: Array<[number, number]>): [number, number] => {
  if (!Array.isArray(coordinates) || coordinates.length === 0) {
    return [0, 0];
  }
  const [sumLat, sumLng] = coordinates.reduce(
    (acc, [lat, lng]) => [acc[0] + Number(lat || 0), acc[1] + Number(lng || 0)],
    [0, 0],
  );
  return [sumLat / coordinates.length, sumLng / coordinates.length];
};

export const normalizeDrawnValue = (raw: any): DrawnMapValue => {
  if (!raw) return getDefaultValue();
  let parsed = raw;
  for (let i = 0; i < 3; i += 1) {
    if (typeof parsed !== "string") break;
    try {
      parsed = JSON.parse(parsed);
    } catch {
      break;
    }
  }

  if (!parsed || typeof parsed !== "object") return getDefaultValue();

  const drawn = (parsed as any).drawn ?? parsed;
  if (drawn && Array.isArray(drawn.folders) && Array.isArray(drawn.polygons)) {
    const safe = {
      version: 1 as const,
      folders: drawn.folders
        .filter((folder: any) => typeof folder?.id === "string" || typeof folder?.id === "number")
        .map((folder: any) => ({
          id: `${folder.id}`,
          name: typeof folder.name === "string" && folder.name.trim()
            ? folder.name.trim()
            : "Dossier",
        })),
      polygons: drawn.polygons
        .filter((polygon: any) => typeof polygon?.id === "string" || typeof polygon?.id === "number")
        .map((polygon: any) => ({
          id: `${polygon.id}`,
          name: resolvePolygonDisplayName(
            polygon.name,
            sanitizeCadastreMetadata(polygon.cadastre),
          ),
          folderId:
            typeof polygon.folderId === "string" || typeof polygon.folderId === "number"
              ? `${polygon.folderId}`
              : DEFAULT_FOLDER_ID,
          coordinates: normalizeLatLngCoordinates(polygon.coordinates),
          cadastre: sanitizeCadastreMetadata(polygon.cadastre),
        })),
      mapView: drawn.mapView,
    };
    safe.polygons = mergeCadastrePolygons(
      safe.polygons,
      Array.isArray((parsed as any).parcel) ? (parsed as any).parcel : [],
    );
    return ensureDefaultFolder(safe);
  }

  const legacyFolders = parseLegacyFolders((parsed as any).folders);
  const legacyLocation = Array.isArray((parsed as any).location) ? (parsed as any).location : [];
  const locationPolygons: DrawnPolygon[] = legacyLocation
    .map((item: any, index: number) => ({
      id: uid(`legacy-${index}`),
      name: DEFAULT_POLYGON_NAME,
      folderId: DEFAULT_FOLDER_ID,
      coordinates: parseLegacyCoordinates(item),
    }))
    .filter((polygon) => polygon.coordinates.length >= 3);
  const parcelPolygons = (Array.isArray((parsed as any).parcel) ? (parsed as any).parcel : [])
    .map((parcel: any, index: number) => createCadastrePolygonFromLegacyParcel(parcel, index))
    .filter((polygon): polygon is DrawnPolygon => !!polygon);

  return ensureDefaultFolder({
    ...getDefaultValue(),
    folders: legacyFolders.length ? legacyFolders : getDefaultValue().folders,
    polygons: [...locationPolygons, ...parcelPolygons],
  });
};

export const serializeDrawnAnswer = (drawn: DrawnMapValue) => {
  const safeDrawn = ensureDefaultFolder(drawn);
  const location: Array<Array<{ lat: number; lng: number }>> = [];
  const parcel: any[] = [];

  safeDrawn.polygons.forEach((polygon) => {
    const normalizedCoordinates = normalizeLatLngCoordinates(polygon.coordinates);
    if (normalizedCoordinates.length < 3) return;

    if (polygon.cadastre) {
      parcel.push({
        ...polygon.cadastre,
        name: polygon.name,
        folder: polygon.folderId,
        coordinates: closeRingAndConvertToLatLng(normalizedCoordinates),
      });
      return;
    }

    location.push(
      normalizedCoordinates.map(([lat, lng]) => ({ lat, lng })),
    );
  });

  return {
    drawn: safeDrawn,
    parcel,
    location,
  };
};

type Props = {
  value: DrawnMapValue;
  onChange: (nextValue: DrawnMapValue) => void;
  readOnly?: boolean;
};

const DraggablePolygonRow = ({
  polygon,
  selected,
  readOnly = false,
  onSelect,
  onRename,
  onDelete,
}: {
  polygon: DrawnPolygon;
  selected: boolean;
  readOnly?: boolean;
  onSelect: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(polygon.name);
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `polygon:${polygon.id}`,
    data: { polygonId: polygon.id },
  });

  useEffect(() => {
    setDraftName(polygon.name);
  }, [polygon.name]);

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.55 : 1,
  } as const;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mb-2 rounded-md border bg-white p-2 ${selected ? "border-[#3b82f6]" : "border-[#e2e8f0]"}`}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={`rounded p-1 text-[#64748b] ${readOnly ? "cursor-default opacity-0 pointer-events-none" : "cursor-pointer hover:bg-[#f1f5f9]"}`}
          {...(!readOnly ? listeners : {})}
          {...(!readOnly ? attributes : {})}
        >
          <GripVertical size={14} />
        </button>
        <button type="button" className="min-w-0 flex-1 text-left" onClick={onSelect}>
          {!editing ? (
            <span className="flex items-center gap-2">
              <span className="block min-w-0 flex-1 truncate text-sm">{polygon.name}</span>
            </span>
          ) : (
            <Input
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              className="h-8"
              autoFocus
            />
          )}
        </button>
        {!readOnly && !editing ? (
          <button type="button" className="rounded p-1 hover:bg-[#f1f5f9]" onClick={() => setEditing(true)}>
            <Pencil size={14} />
          </button>
        ) : !readOnly && editing ? (
          <button
            type="button"
            className="rounded p-1 text-green-700 hover:bg-[#ecfdf3]"
            onClick={() => {
              onRename(draftName.trim() || DEFAULT_POLYGON_NAME);
              setEditing(false);
            }}
          >
            <Check size={14} />
          </button>
        ) : null}
        {!readOnly && (
          <button type="button" className="rounded p-1 text-red-600 hover:bg-[#fef2f2]" onClick={onDelete}>
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

const DroppableFolderSection = ({
  folder,
  polygons,
  selectedPolygonId,
  readOnly = false,
  onCreatePolygonName,
  onSelectPolygon,
  onRenamePolygon,
  onDeletePolygon,
  onRenameFolder,
  onDeleteFolder,
}: {
  folder: DrawnFolder;
  polygons: DrawnPolygon[];
  selectedPolygonId: string | null;
  readOnly?: boolean;
  onCreatePolygonName: (folderId: string) => void;
  onSelectPolygon: (polygonId: string) => void;
  onRenamePolygon: (polygonId: string, nextName: string) => void;
  onDeletePolygon: (polygonId: string) => void;
  onRenameFolder: (folderId: string, nextName: string) => void;
  onDeleteFolder: (folderId: string) => void;
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: `folder:${folder.id}` });
  const [editingFolder, setEditingFolder] = useState(false);
  const [folderNameDraft, setFolderNameDraft] = useState(folder.name);

  useEffect(() => {
    setFolderNameDraft(folder.name);
  }, [folder.name]);

  return (
    <div
      ref={setNodeRef}
      className={`mb-3 rounded-md border p-2 ${
        isOver ? "border-[#3b82f6] bg-[#eff6ff]" : "border-[#e2e8f0] bg-[#f8fafc]"
      }`}
    >
      <div className="mb-2 flex items-center gap-2">
        {!editingFolder ? (
          <strong className="min-w-0 flex-1 truncate text-sm">
            {folder.name} - ({polygons.length})
          </strong>
        ) : (
          <Input
            className="h-8 min-w-0 flex-1"
            value={folderNameDraft}
            onChange={(e) => setFolderNameDraft(e.target.value)}
            autoFocus
          />
        )}
        {!readOnly && !editingFolder ? (
          <button type="button" className="rounded p-1 hover:bg-[#e2e8f0]" onClick={() => setEditingFolder(true)}>
            <Pencil size={14} />
          </button>
        ) : !readOnly && editingFolder ? (
          <button
            type="button"
            className="rounded p-1 text-green-700 hover:bg-[#ecfdf3]"
            onClick={() => {
              onRenameFolder(folder.id, folderNameDraft.trim() || "Dossier");
              setEditingFolder(false);
            }}
          >
            <Check size={14} />
          </button>
        ) : null}
        {!readOnly && folder.id !== DEFAULT_FOLDER_ID && (
          <button
            type="button"
            className="rounded p-1 text-red-600 hover:bg-[#fef2f2]"
            onClick={() => onDeleteFolder(folder.id)}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {!readOnly && (
        <button
          type="button"
          className="mb-2 rounded-md border border-dashed border-[#cbd5e1] px-2 py-1 text-xs hover:bg-white"
          onClick={() => onCreatePolygonName(folder.id)}
        >
          Nouveau polygone ici
        </button>
      )}

      {polygons.map((polygon) => (
        <DraggablePolygonRow
          key={polygon.id}
          polygon={polygon}
          selected={selectedPolygonId === polygon.id}
          readOnly={readOnly}
          onSelect={() => onSelectPolygon(polygon.id)}
          onRename={(nextName) => onRenamePolygon(polygon.id, nextName)}
          onDelete={() => onDeletePolygon(polygon.id)}
        />
      ))}
    </div>
  );
};

const DrawnMapField = ({ value, onChange, readOnly = false }: Props) => {
  const { toast } = useToast();
  const mapRef = useRef<L.Map | null>(null);
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const drawnFeatureGroupRef = useRef<L.FeatureGroup | null>(null);
  const cadastreFeatureGroupRef = useRef<L.FeatureGroup | null>(null);
  const layerByPolygonIdRef = useRef<Map<string, L.Polygon>>(new Map());
  const markerByPolygonIdRef = useRef<Map<string, L.Marker>>(new Map());
  const cadastreLayerByKeyRef = useRef<Map<string, L.Polygon>>(new Map());
  const cadastreRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cadastreFetchAbortRef = useRef<AbortController | null>(null);
  const cadastreRequestIdRef = useRef(0);
  const mapPointIconRef = useRef<L.Icon | null>(null);
  const [mapZoom, setMapZoom] = useState<number>(value?.mapView?.zoom ?? 5);
  const [isFrenchViewport, setIsFrenchViewport] = useState<boolean>(() => {
    const center = value?.mapView?.center ?? [48.8566, 2.3522];
    return checkIfInFrance(center[0], center[1]);
  });
  const [selectedFolderId, setSelectedFolderId] = useState<string>(DEFAULT_FOLDER_ID);
  const [selectedPolygonId, setSelectedPolygonId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<Array<{ display_name: string; lat: string; lon: string }>>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dataRef = useRef<DrawnMapValue>(ensureDefaultFolder(value));
  const selectedFolderIdRef = useRef<string>(selectedFolderId);
  const selectFolder = (folderId: string) => {
    selectedFolderIdRef.current = folderId;
    setSelectedFolderId(folderId);
  };

  const data = useMemo(() => ensureDefaultFolder(value), [value]);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    selectedFolderIdRef.current = selectedFolderId;
  }, [selectedFolderId]);

  const updateData = useCallback((updater: (current: DrawnMapValue) => DrawnMapValue) => {
    const updatedValue = updater(dataRef.current);
    const map = mapRef.current;
    const nextMapView = map
      ? {
        center: [map.getCenter().lat, map.getCenter().lng] as [number, number],
        zoom: map.getZoom(),
      }
      : updatedValue.mapView ?? dataRef.current.mapView;

    const nextValue = ensureDefaultFolder({
      ...updatedValue,
      mapView: nextMapView,
    });
    dataRef.current = nextValue;
    onChange(nextValue);
  }, [onChange]);
  const updateDataRef = useRef(updateData);
  const toastRef = useRef(toast);

  useEffect(() => {
    updateDataRef.current = updateData;
  }, [updateData]);

  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const clearCadastreOverlay = useCallback(() => {
    const cadastreFeatureGroup = cadastreFeatureGroupRef.current;
    if (cadastreFeatureGroup) {
      cadastreFeatureGroup.eachLayer((layer) => {
        cadastreFeatureGroup.removeLayer(layer);
      });
    }
    cadastreLayerByKeyRef.current.clear();
  }, []);

  const refreshCadastreOverlay = useCallback(async () => {
    const map = mapRef.current;
    const cadastreFeatureGroup = cadastreFeatureGroupRef.current;
    if (!map || !cadastreFeatureGroup) return;

    const center = map.getCenter();
    const zoom = map.getZoom();
    const inFrance = checkIfInFrance(center.lat, center.lng);
    setIsFrenchViewport(inFrance);

    if (!inFrance || zoom < CADASTRE_MIN_ZOOM) {
      cadastreFetchAbortRef.current?.abort();
      cadastreFetchAbortRef.current = null;
      clearCadastreOverlay();
      return;
    }

    cadastreFetchAbortRef.current?.abort();
    const abortController = new AbortController();
    cadastreFetchAbortRef.current = abortController;
    const requestId = ++cadastreRequestIdRef.current;

    let p = 10;
    if (zoom <= 16) {
      p = 30 + (16 - zoom) * 60;
    }
    const radius = zoom * p;
    const circle = turf.circle(turf.point([center.lng, center.lat]), radius, {
      steps: 64,
      units: "meters",
    });
    const encodedGeom = encodeURIComponent(JSON.stringify({
      type: "MultiPolygon",
      coordinates: [circle.geometry.coordinates],
    }));

    try {
      const response = await fetch(
        `https://apicarto.ign.fr/api/cadastre/parcelle?geom=${encodedGeom}`,
        { signal: abortController.signal },
      );
      const payload = await response.json();
      if (abortController.signal.aborted) return;
      if (requestId !== cadastreRequestIdRef.current) return;

      const features = Array.isArray(payload?.features) ? payload.features : [];
      const selectedCadastreKeys = new Set(
        dataRef.current.polygons
          .map((polygon) => getCadastreKey(polygon.cadastre))
          .filter(Boolean),
      );

      clearCadastreOverlay();

      features.forEach((feature: any, index: number) => {
        const cadastre = sanitizeCadastreMetadata(dataProcessingParcel(feature, 0, true));
        const coordinates = parseParcelCoordinatesToLatLng(feature?.geometry?.coordinates, cadastre);
        if (coordinates.length < 3) return;
        const cadastreKey = getCadastreKey(cadastre) || `${index}`;
        if (selectedCadastreKeys.has(cadastreKey)) return;

        const layer = L.polygon(coordinates, {
          color: "#eab308",
          weight: 1,
          fillColor: "#facc15",
          fillOpacity: 0.18,
        });

        const parcelLabel = getCadastreName(cadastre, "Parcelle");
        const communeLabel = typeof cadastre?.nomCom === "string" ? cadastre.nomCom : "";
        layer.bindTooltip(
          `<b>Parcelle:</b> ${parcelLabel}${communeLabel ? `<br/><b>Commune:</b> ${communeLabel}` : ""}`,
        );

        layer.on("mouseover", () => {
          layer.setStyle({ fillOpacity: 0.32, weight: 1.5 });
        });
        layer.on("mouseout", () => {
          layer.setStyle({ fillOpacity: 0.18, weight: 1 });
        });
        layer.on("click", () => {
          if (readOnly) return;
          const folderId = selectedFolderIdRef.current || DEFAULT_FOLDER_ID;
          let createdPolygonId: string | null = null;

          updateDataRef.current((current) => {
            const alreadyExists = current.polygons.some(
              (polygon) => getCadastreKey(polygon.cadastre) === cadastreKey,
            );
            if (alreadyExists) {
              return current;
            }
            createdPolygonId = uid("cadastre");
            return {
              ...current,
              polygons: [
                ...current.polygons,
                {
                  id: createdPolygonId,
                  name: DEFAULT_POLYGON_NAME,
                  folderId,
                  coordinates,
                  cadastre,
                },
              ],
            };
          });

          if (createdPolygonId) {
            setSelectedPolygonId(createdPolygonId);
            selectFolder(folderId);
          }

          cadastreFeatureGroup.removeLayer(layer);
          cadastreLayerByKeyRef.current.delete(cadastreKey);
        });

        layer.addTo(cadastreFeatureGroup);
        cadastreLayerByKeyRef.current.set(cadastreKey, layer);
      });
    } catch (error: any) {
      if (error?.name === "AbortError") return;
      console.error("[DrawnMapField][cadastre]", error);
    } finally {
      if (cadastreFetchAbortRef.current === abortController) {
        cadastreFetchAbortRef.current = null;
      }
    }
  }, [clearCadastreOverlay, readOnly]);

  const scheduleCadastreRefresh = useCallback((delay = 150) => {
    if (cadastreRefreshTimerRef.current) {
      clearTimeout(cadastreRefreshTimerRef.current);
    }
    cadastreRefreshTimerRef.current = setTimeout(() => {
      cadastreRefreshTimerRef.current = null;
      void refreshCadastreOverlay();
    }, delay);
  }, [refreshCadastreOverlay]);

  if (!mapPointIconRef.current) {
    mapPointIconRef.current = L.icon({
      iconUrl: "/pinco.png",
      iconSize: [28, 28],
      iconAnchor: [5, 12],
      popupAnchor: [0, -32],
    });
  }

  useEffect(() => {
    if (!mapElementRef.current || mapRef.current) return;

    const map = L.map(mapElementRef.current, {
      center: data.mapView?.center ?? [48.8566, 2.3522],
      zoom: data.mapView?.zoom ?? 5,
    });
    setMapZoom(map.getZoom());
    mapRef.current = map;

    const stadiaApiKey = import.meta.env.VITE_API_KEY_STADIAMAP;
    if (typeof stadiaApiKey === "string" && stadiaApiKey.trim()) {
      L.tileLayer(
        `https://tiles.stadiamaps.com/tiles/alidade_satellite/{z}/{x}/{y}{r}.{ext}?api_key=${stadiaApiKey}`,
        {
          attribution:
            '&copy; CNES, Distribution Airbus DS, © Airbus DS, © PlanetObserver (Contains Copernicus Data) | &copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          ext: "jpg",
        },
      ).addTo(map);
    } else {
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(map);
    }

    const featureGroup = L.featureGroup().addTo(map);
    drawnFeatureGroupRef.current = featureGroup;
    const cadastreFeatureGroup = L.featureGroup().addTo(map);
    cadastreFeatureGroupRef.current = cadastreFeatureGroup;

    const drawControl = new (L.Control as any).Draw({
      position: "topleft",
      draw: {
        polygon: {
          allowIntersection: false,
          shapeOptions: {
            color: "#3b82f6",
            fillColor: "#3b82f6",
            fillOpacity: 0.2,
            weight: 2,
          },
        },
        polyline: false,
        rectangle: false,
        circle: false,
        marker: false,
        circlemarker: false,
      },
      edit: {
        featureGroup,
      },
    });
    map.addControl(drawControl);

    map.on("draw:created", (event: any) => {
      if (readOnly) return;
      const layer = event.layer as L.Polygon;
      const latlngs = layer.getLatLngs();
      const ring = Array.isArray(latlngs) && Array.isArray(latlngs[0]) ? latlngs[0] : [];
      const coordinates = ring
        .map((point: any) => [Number(point.lat), Number(point.lng)] as [number, number])
        .filter((point) => Number.isFinite(point[0]) && Number.isFinite(point[1]));

      if (coordinates.length < 3) {
        toastRef.current({
          title: "Polygone invalide",
          className: "text-red-600",
        });
        return;
      }

      const polygonId = uid("polygon");
      const folderId = selectedFolderIdRef.current || DEFAULT_FOLDER_ID;

      updateDataRef.current((current) => ({
        ...current,
        polygons: [
          ...current.polygons,
          {
            id: polygonId,
            name: DEFAULT_POLYGON_NAME,
            folderId,
            coordinates,
          },
        ],
      }));
      setSelectedPolygonId(polygonId);
    });

    const drawToolbar = mapElementRef.current?.querySelector(".leaflet-draw");
    if (drawToolbar instanceof HTMLElement) {
      drawToolbar.style.display = readOnly ? "none" : "";
    }

    const handleViewportChange = () => {
      setMapZoom(map.getZoom());
      scheduleCadastreRefresh();
    };

    map.on("zoomend", handleViewportChange);
    map.on("moveend", handleViewportChange);
    handleViewportChange();

    return () => {
      if (cadastreRefreshTimerRef.current) {
        clearTimeout(cadastreRefreshTimerRef.current);
        cadastreRefreshTimerRef.current = null;
      }
      cadastreFetchAbortRef.current?.abort();
      cadastreFetchAbortRef.current = null;
      clearCadastreOverlay();
      map.remove();
      mapRef.current = null;
      drawnFeatureGroupRef.current = null;
      cadastreFeatureGroupRef.current = null;
      layerByPolygonIdRef.current.clear();
      markerByPolygonIdRef.current.clear();
      cadastreLayerByKeyRef.current.clear();
    };
  }, [clearCadastreOverlay, readOnly, scheduleCadastreRefresh]);

  useEffect(() => {
    const drawToolbar = mapElementRef.current?.querySelector(".leaflet-draw");
    if (drawToolbar instanceof HTMLElement) {
      drawToolbar.style.display = readOnly ? "none" : "";
    }
  }, [readOnly]);

  useEffect(() => {
    const map = mapRef.current;
    const featureGroup = drawnFeatureGroupRef.current;
    if (!map || !featureGroup) return;

    const existingIds = new Set(data.polygons.map((polygon) => polygon.id));

    Array.from(layerByPolygonIdRef.current.entries()).forEach(([polygonId, layer]) => {
      if (!existingIds.has(polygonId)) {
        featureGroup.removeLayer(layer);
        layerByPolygonIdRef.current.delete(polygonId);
      }
    });
    Array.from(markerByPolygonIdRef.current.entries()).forEach(([polygonId, marker]) => {
      if (!existingIds.has(polygonId)) {
        featureGroup.removeLayer(marker);
        markerByPolygonIdRef.current.delete(polygonId);
      }
    });

    data.polygons.forEach((polygon) => {
      const existingLayer = layerByPolygonIdRef.current.get(polygon.id);
      const existingMarker = markerByPolygonIdRef.current.get(polygon.id);
      const latlngs = polygon.coordinates.map(([lat, lng]) => L.latLng(lat, lng));
      if (latlngs.length < 3) return;
      const centroid = getPolygonCentroid(polygon.coordinates);
      const centroidLatLng = L.latLng(centroid[0], centroid[1]);
      if (!existingLayer) {
        const layer = L.polygon(latlngs, {
          color: selectedPolygonId === polygon.id ? "#1d4ed8" : "#3b82f6",
          fillColor: "#60a5fa",
          fillOpacity: selectedPolygonId === polygon.id ? 0.35 : 0.2,
          weight: selectedPolygonId === polygon.id ? 3 : 2,
        });
        layer.on("click", () => {
          setSelectedPolygonId(polygon.id);
          selectFolder(polygon.folderId);
        });
        layerByPolygonIdRef.current.set(polygon.id, layer);
      } else {
        existingLayer.setLatLngs(latlngs);
        existingLayer.setStyle({
          color: selectedPolygonId === polygon.id ? "#1d4ed8" : "#3b82f6",
          fillOpacity: selectedPolygonId === polygon.id ? 0.35 : 0.2,
          weight: selectedPolygonId === polygon.id ? 3 : 2,
        });
      }

      if (!existingMarker) {
        const marker = L.marker(centroidLatLng, {
          icon: mapPointIconRef.current as L.Icon,
          title: polygon.name,
        });
        marker.on("click", () => {
          setSelectedPolygonId(polygon.id);
          selectFolder(polygon.folderId);
        });
        markerByPolygonIdRef.current.set(polygon.id, marker);
      } else {
        existingMarker.setLatLng(centroidLatLng);
        existingMarker.options.title = polygon.name;
      }
    });

    data.polygons.forEach((polygon) => {
      const layer = layerByPolygonIdRef.current.get(polygon.id);
      const marker = markerByPolygonIdRef.current.get(polygon.id);
      if (!layer || !marker) return;

      const isSelected = selectedPolygonId === polygon.id;
      const polygonZoomThreshold = isSelected
        ? SELECTED_POLYGON_VISIBILITY_ZOOM_THRESHOLD
        : POLYGON_VISIBILITY_ZOOM_THRESHOLD;
      const showPolygon = mapZoom >= polygonZoomThreshold;

      if (showPolygon) {
        if (!featureGroup.hasLayer(layer)) {
          layer.addTo(featureGroup);
        }
        if (featureGroup.hasLayer(marker)) {
          featureGroup.removeLayer(marker);
        }
      } else {
        if (!featureGroup.hasLayer(marker)) {
          marker.addTo(featureGroup);
        }
        if (featureGroup.hasLayer(layer)) {
          featureGroup.removeLayer(layer);
        }
      }
    });
  }, [data.polygons, mapZoom, selectedPolygonId]);

  useEffect(() => {
    scheduleCadastreRefresh(50);
  }, [data.polygons, scheduleCadastreRefresh]);

  useEffect(() => {
    if (!query || query.length < 3) {
      setSearchResults([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&limit=6&q=${encodeURIComponent(query)}`,
        );
        const payload = await response.json();
        if (!cancelled) {
          setSearchResults(Array.isArray(payload) ? payload : []);
        }
      } catch {
        if (!cancelled) {
          setSearchResults([]);
        }
      } finally {
        if (!cancelled) {
          setSearchLoading(false);
        }
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const polygonsByFolder = useMemo(() => {
    const result: Record<string, DrawnPolygon[]> = {};
    data.folders.forEach((folder) => {
      result[folder.id] = [];
    });
    data.polygons.forEach((polygon) => {
      const folderId = result[polygon.folderId] ? polygon.folderId : DEFAULT_FOLDER_ID;
      if (!result[folderId]) result[folderId] = [];
      result[folderId].push(polygon);
    });
    return result;
  }, [data.folders, data.polygons]);

  const movePolygonToFolder = (polygonId: string, folderId: string) => {
    updateData((current) => ({
      ...current,
      polygons: current.polygons.map((polygon) =>
        polygon.id === polygonId ? { ...polygon, folderId } : polygon,
      ),
    }));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (readOnly) return;
    const activeId = `${event.active.id}`;
    const overId = event.over ? `${event.over.id}` : "";
    if (!activeId.startsWith("polygon:")) return;
    const polygonId = activeId.replace("polygon:", "");
    if (!polygonId) return;

    if (overId.startsWith("folder:")) {
      const folderId = overId.replace("folder:", "");
      if (folderId) {
        movePolygonToFolder(polygonId, folderId);
      }
      return;
    }
    if (overId.startsWith("polygon:")) {
      const targetPolygonId = overId.replace("polygon:", "");
      const targetPolygon = data.polygons.find((polygon) => polygon.id === targetPolygonId);
      if (targetPolygon) {
        movePolygonToFolder(polygonId, targetPolygon.folderId);
      }
    }
  };

  const startDraw = () => {
    if (readOnly) return;
    const map = mapRef.current;
    if (!map) return;
    const drawPolygon = new (L.Draw as any).Polygon(map, {
      shapeOptions: {
        color: "#3b82f6",
        fillColor: "#3b82f6",
        fillOpacity: 0.2,
        weight: 2,
      },
      allowIntersection: false,
    });
    drawPolygon.enable();
  };

  const importKml = async (file: File) => {
    if (readOnly) return;
    if (!file.name.toLowerCase().endsWith(".kml")) {
      toast({
        title: "Seul le format KML est accepte",
        className: "text-red-600",
      });
      return;
    }

    const text = await file.text();
    const parser = new DOMParser();
    const dom = parser.parseFromString(text, "application/xml");
    const geojson = toGeoJSON.kml(dom as any) as any;
    const features = Array.isArray(geojson?.features) ? geojson.features : [];
    const nextPolygons: DrawnPolygon[] = [];

    features.forEach((feature: any) => {
      const name =
        typeof feature?.properties?.name === "string" && feature.properties.name.trim()
          ? feature.properties.name.trim()
          : "Polygone importe";
      const geometry = feature?.geometry;
      if (!geometry?.type) return;

      if (geometry.type === "Polygon" && Array.isArray(geometry.coordinates?.[0])) {
        const ring = geometry.coordinates[0];
        const coordinates = ring
          .map((point: any) => (Array.isArray(point) && point.length >= 2
            ? [Number(point[1]), Number(point[0])] as [number, number]
            : null))
          .filter((point: any) => Array.isArray(point));
        if (coordinates.length >= 3) {
          nextPolygons.push({
            id: uid("kml"),
            name,
            folderId: selectedFolderIdRef.current || DEFAULT_FOLDER_ID,
            coordinates,
          });
        }
      }

      if (geometry.type === "MultiPolygon" && Array.isArray(geometry.coordinates)) {
        geometry.coordinates.forEach((polygonCoords: any, index: number) => {
          const ring = Array.isArray(polygonCoords?.[0]) ? polygonCoords[0] : [];
          const coordinates = ring
            .map((point: any) => (Array.isArray(point) && point.length >= 2
              ? [Number(point[1]), Number(point[0])] as [number, number]
              : null))
            .filter((point: any) => Array.isArray(point));
          if (coordinates.length >= 3) {
            nextPolygons.push({
              id: uid("kml"),
              name: `${name} ${index + 1}`,
              folderId: selectedFolderIdRef.current || DEFAULT_FOLDER_ID,
              coordinates,
            });
          }
        });
      }
    });

    if (nextPolygons.length === 0) {
      toast({
        title: "Aucun polygone detecte dans le fichier KML",
        className: "text-red-600",
      });
      return;
    }

    updateData((current) => ({
      ...current,
      polygons: [...current.polygons, ...nextPolygons],
    }));

    toast({
      title: `${nextPolygons.length} polygone(s) importe(s)`,
    });
  };

  return (
    <div className="w-full rounded-xl border border-[#dbe4d2] bg-white">
      <div className="border-b border-[#e5ebe0] p-3">
        <div className="flex flex-wrap items-center gap-2">
          {!readOnly && (
            <>
              <Button type="button" onClick={startDraw}>
                Tracer une parcelle
              </Button>
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload size={14} />
                Importer KML
              </Button>
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                onClick={() => {
                  const folderId = uid("folder");
                  updateData((current) => ({
                    ...current,
                    folders: [...current.folders, { id: folderId, name: "Nouveau dossier" }],
                  }));
                  selectFolder(folderId);
                }}
              >
                <FolderPlus size={14} />
                Ajouter un dossier
              </Button>

              <input
                ref={fileInputRef}
                type="file"
                accept=".kml"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    await importKml(file);
                  }
                  e.target.value = "";
                }}
              />
            </>
          )}
        </div>

        <div className="relative mt-3 max-w-[520px]">
          <Search size={14} className="pointer-events-none absolute left-3 top-3 text-[#64748b]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            placeholder="Rechercher un lieu..."
          />
          {query.length >= 3 && (
            <div className="absolute left-0 top-[44px] z-[2000] max-h-[220px] w-full overflow-auto rounded-md border bg-white shadow">
              {searchLoading && <p className="px-3 py-2 text-sm text-[#64748b]">Recherche...</p>}
              {!searchLoading && searchResults.length === 0 && (
                <p className="px-3 py-2 text-sm text-[#64748b]">Aucun resultat</p>
              )}
              {!searchLoading && searchResults.map((result, index) => (
                <button
                  key={`${result.lat}-${result.lon}-${index}`}
                  type="button"
                  className="block w-full border-b px-3 py-2 text-left text-sm hover:bg-[#f8fafc]"
                  onClick={() => {
                    const lat = Number(result.lat);
                    const lng = Number(result.lon);
                    if (mapRef.current && Number.isFinite(lat) && Number.isFinite(lng)) {
                      mapRef.current.flyTo([lat, lng], 14, { duration: 0.6 });
                    }
                    setQuery(result.display_name);
                    setSearchResults([]);
                  }}
                >
                  {result.display_name}
                </button>
              ))}
            </div>
          )}
        </div>

        <p className="mt-2 text-xs text-[#64748b]">
          {isFrenchViewport
            ? mapZoom >= CADASTRE_MIN_ZOOM
              ? "France detectee: cliquez une parcelle cadastrale pour l'ajouter au dossier selectionne."
              : `France detectee: zoomez a ${CADASTRE_MIN_ZOOM}+ pour afficher les parcelles cadastrales.`
            : "Hors France: trace manuel ou import KML."}
        </p>
      </div>

      <div className="flex h-[620px] w-full overflow-hidden">
        <div className="w-[360px] shrink-0 border-r border-[#e5ebe0] bg-[#f8fafc]">
          <ScrollArea className="h-full p-3">
            <DndContext onDragEnd={handleDragEnd}>
              {data.folders.map((folder) => (
                <DroppableFolderSection
                  key={folder.id}
                  folder={folder}
                  polygons={polygonsByFolder[folder.id] ?? []}
                  selectedPolygonId={selectedPolygonId}
                  readOnly={readOnly}
                  onCreatePolygonName={(folderId) => {
                    selectFolder(folderId);
                    startDraw();
                  }}
                  onSelectPolygon={(polygonId) => {
                    setSelectedPolygonId(polygonId);
                    const polygon = data.polygons.find((item) => item.id === polygonId);
                    if (!polygon) return;
                    const layer = layerByPolygonIdRef.current.get(polygonId);
                    if (layer && mapRef.current) {
                      mapRef.current.fitBounds(layer.getBounds(), { padding: [20, 20] });
                    }
                  }}
                  onRenamePolygon={(polygonId, nextName) => {
                    updateData((current) => ({
                      ...current,
                      polygons: current.polygons.map((polygon) =>
                        polygon.id === polygonId ? { ...polygon, name: nextName } : polygon,
                      ),
                    }));
                  }}
                  onDeletePolygon={(polygonId) => {
                    if (selectedPolygonId === polygonId) {
                      setSelectedPolygonId(null);
                    }
                    updateData((current) => ({
                      ...current,
                      polygons: current.polygons.filter((polygon) => polygon.id !== polygonId),
                    }));
                  }}
                  onRenameFolder={(folderId, nextName) => {
                    updateData((current) => ({
                      ...current,
                      folders: current.folders.map((folderItem) =>
                        folderItem.id === folderId ? { ...folderItem, name: nextName } : folderItem,
                      ),
                    }));
                  }}
                  onDeleteFolder={(folderId) => {
                    if (folderId === DEFAULT_FOLDER_ID) return;
                    updateData((current) => ({
                      ...current,
                      folders: current.folders.filter((folderItem) => folderItem.id !== folderId),
                      polygons: current.polygons.map((polygon) =>
                        polygon.folderId === folderId
                          ? { ...polygon, folderId: DEFAULT_FOLDER_ID }
                          : polygon,
                      ),
                    }));
                    selectFolder(DEFAULT_FOLDER_ID);
                  }}
                />
              ))}
            </DndContext>
          </ScrollArea>
        </div>

        <div className="min-w-0 flex-1">
          <div ref={mapElementRef} className="h-full w-full" />
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-[#e5ebe0] px-3 py-2 text-xs text-[#64748b]">
        <span>{data.polygons.length} polygone(s)</span>
        {!readOnly && selectedPolygonId && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-red-600 hover:underline"
            onClick={() => {
              updateData((current) => ({
                ...current,
                polygons: current.polygons.filter((polygon) => polygon.id !== selectedPolygonId),
              }));
              setSelectedPolygonId(null);
            }}
          >
            <X size={12} />
            Supprimer la selection
          </button>
        )}
      </div>
    </div>
  );
};

export default DrawnMapField;
